import { useEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";

import { useAppSelector } from "../../../../app/store";
import { cn } from "../../../../lib/utils";
import { getProducts } from "../../../configs/logic/Slice";
import ButtonV2 from "../../../../components/ui/button/v2";
import { OptionNameValuePair } from "../../../../lib/interfaces/types";

import {
  CREDIT_FACILITY_OPTIONS,
  INLINE_ROW_LIMIT,
  LoanAccount,
} from "../../../../api/creditAccounts";
import { buildEmptyLoanAccount } from "../../utils/creditFactories";
import { getAccountRowErrors } from "../../utils/creditValidation";
import { SectionIssue } from "./derive";
import FormField, { useTouched } from "./FormField";
import { SectionAccordion, CollectionModal, ViewMoreButton } from "./SectionShell";
import { CreditAccountProduct } from "../../../../api/credit_accounts";
import AccountHolderIcon from "../../../creditAccounts/components/AccountHolderIcon";
import AddLoanAccountButton from "../../../creditAccounts/components/AddLoanAccountButton";

interface RowProps {
  row: LoanAccount;
  all: LoanAccount[];
  productOptions: OptionNameValuePair<string>[];
  showErrors: boolean;
  showLabels: boolean;
  onChange: (patch: Partial<LoanAccount>) => void;
  onDelete: () => void;
}

const LoanAccountRow: React.FC<RowProps> = ({ row, all, productOptions, showErrors, showLabels, onChange, onDelete }) => {
  const { isTouched, markTouched } = useTouched();
  const errors = getAccountRowErrors(row, all);
  const err = (f: string) => (showErrors || isTouched(f) ? errors[f] : undefined);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-start">
      <FormField type="text" label="Loan Account Number" required placeholder="Enter account number"
        value={row.extAccountNumber} error={err("extAccountNumber")} hideLabel={!showLabels}
        onChange={(v) => onChange({ extAccountNumber: v })} onBlur={() => markTouched("extAccountNumber")} />
      <FormField type="select" label="Product" required placeholder="Select product"
        value={row.product} options={productOptions} error={err("product")} hideLabel={!showLabels}
        onChange={(v) => onChange({ product: v })} onBlur={() => markTouched("product")} />
      <FormField type="select" label="Credit Facility" required placeholder="Select type"
        value={row.creditFacility} options={CREDIT_FACILITY_OPTIONS} error={err("creditFacility")} hideLabel={!showLabels}
        onChange={(v) => onChange({ creditFacility: v })} onBlur={() => markTouched("creditFacility")} />
      <FormField type="text" label="Account Name (Primary Borrower)" required placeholder="Enter name"
        value={row.accountName} error={err("accountName")} hideLabel={!showLabels}
        onChange={(v) => onChange({ accountName: v })} onBlur={() => markTouched("accountName")}
        prefixIcon={row.product ? <AccountHolderIcon product={row.product as CreditAccountProduct} /> : undefined} />
      <div className={cn("flex items-center", showLabels && "pt-5")}>
        <ButtonV2 variant="outline" size="sm" onClick={onDelete} title="Delete loan account"
          className="h-9 px-2 rounded-md bg-white border border-gray-300 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500"
          prefix={<Trash2 width="16" height="16" />} />
      </div>
    </div>
  );
};

interface LoanAccountsSectionProps {
  loanAccounts: LoanAccount[];
  onChange: (next: LoanAccount[]) => void;
  showErrors: boolean;
  issue: SectionIssue;
  focus: { key: string; seq: number };
}

const LoanAccountsSection: React.FC<LoanAccountsSectionProps> = ({
  loanAccounts,
  onChange,
  showErrors,
  issue,
  focus,
}) => {
  const products = useAppSelector(getProducts);
  const [modalOpen, setModalOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const productOptions: OptionNameValuePair<string>[] = products.map((p) => ({ name: p, value: p }));
  const addEnabled = issue.incompleteRows === 0;

  // Scroll into view (and open the modal for hidden rows) when this section is focused.
  useEffect(() => {
    if (focus.key !== "loanAccounts") return;
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (issue.hiddenInvalid > 0) setModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus.seq]);

  const add = () => {
    // Beyond the inline limit, new rows are added/managed in the modal.
    const next = [...loanAccounts, buildEmptyLoanAccount()];
    onChange(next);
    if (next.length > INLINE_ROW_LIMIT) setModalOpen(true);
  };
  const change = (accountNumber: string, patch: Partial<LoanAccount>) =>
    onChange(
      loanAccounts.map((a) =>
        a.accountNumber === accountNumber ? { ...a, ...patch } : a,
      ),
    );
  const remove = (accountNumber: string) =>
    onChange(loanAccounts.filter((a) => a.accountNumber !== accountNumber));

  const renderRows = (list: LoanAccount[]) => (
    <div className="flex flex-col gap-4">
      {list.map((row, index) => (
        <LoanAccountRow 
          key={row.accountNumber} 
          row={row} all={loanAccounts} 
          productOptions={productOptions}
          showErrors={showErrors} 
          showLabels={index === 0}
          onChange={(patch) => change(row.accountNumber, patch)} 
          onDelete={() => remove(row.accountNumber)} 
          />
      ))}
    </div>
  );

  const addButton = <AddLoanAccountButton disabled={!addEnabled} onClick={add} />;

  const csvToggle = (
    <div className="flex items-center rounded-md bg-gray-100 p-0.5 text-xs">
      <span className="px-2 py-1 rounded bg-white shadow-sm font-medium">Enter Manually</span>
      <span className="px-2 py-1 text-gray-400 cursor-not-allowed" title="CSV upload coming in a later release">
        Upload CSV
      </span>
    </div>
  );

  return (
    <>
      <div ref={rootRef}>
        <SectionAccordion title="Loan Accounts" count={loanAccounts.length}
          invalidCount={showErrors ? issue.incompleteRows : 0}
          onExpand={() => setModalOpen(true)} rightSlot={csvToggle}>
          <div className="flex flex-col gap-4">
            {renderRows(loanAccounts.slice(0, INLINE_ROW_LIMIT))}
            <div className="flex items-center justify-between">
              {addButton}
              <ViewMoreButton overflowCount={loanAccounts.length - INLINE_ROW_LIMIT}
                singular="account" plural="accounts"
                invalidCount={showErrors ? issue.hiddenInvalid : 0}
                onClick={() => setModalOpen(true)} />
            </div>
          </div>
        </SectionAccordion>
      </div>

      {modalOpen && (
        <CollectionModal title="Loan Accounts" onClose={() => setModalOpen(false)} onDone={() => setModalOpen(false)}>
          <div className="flex flex-col gap-4">
            {renderRows(loanAccounts)}
            {addButton}
          </div>
        </CollectionModal>
      )}
    </>
  );
};

export default LoanAccountsSection;
